import shortId from 'shortid';

import { DatalineStatus, LogicalOperator } from 'src/constants/dataline';
import type { ComparisonFrontend, OriginalDataline } from 'src/types/dataline';
import type { DragItem } from 'src/types/workspace';
import {
  findDataPosition,
  generateDatalineDefinition,
  joinDefinitionlines,
  mapDragItemToComparisonProperties,
  splitDefinitionline,
  validateDataline,
} from 'src/components/workspace/helper/helper';

/*
 * Outsourced helper functions from the workspace context provider
 */

export const updateDatalineNameHelper = (
  datalines: OriginalDataline[],
  datalineName: string,
  datalineRenderId: string,
): OriginalDataline[] =>
  datalines.map((dataline) => {
    if (dataline.renderId === datalineRenderId) {
      const isSaved = datalineName === dataline.name && dataline.isSaved;
      const hasCustomName =
        !(datalineName === dataline.name && !dataline.has_custom_name) ||
        datalineName !== dataline.name;
      const renamedDataline = {
        ...dataline,
        name: datalineName,
        has_custom_name: hasCustomName,
        isSaved,
      };
      const invalidities = validateDataline(renamedDataline);

      return { ...renamedDataline, invalidities };
    }

    return dataline;
  });

export const updateDatalineOrderHelper = (
  datalines: OriginalDataline[],
  dragIndex: number,
  hoverIndex: number,
): OriginalDataline[] => {
  const reorderedDatalines = [...datalines];
  const selectedDataline = reorderedDatalines[dragIndex];

  reorderedDatalines.splice(dragIndex, 1);
  reorderedDatalines.splice(hoverIndex, 0, selectedDataline);

  return reorderedDatalines;
};

export const createDatalineHelper = (
  dragItem: DragItem,
  currentDatalineCount: number,
): OriginalDataline => {
  const comparisonProps = mapDragItemToComparisonProperties(
    dragItem,
    shortId.generate(),
  );
  const dataline = {
    id: null,
    invalidities: false,
    isSaved: false,
    definitionlines: [
      {
        comparisons: [
          {
            ...comparisonProps,
            op: LogicalOperator.AND,
          },
        ],
        renderId: shortId.generate(),
      },
    ],
    driverAnalysis: currentDatalineCount === 0,
    has_custom_name: false,
    name: '',
    position: currentDatalineCount,
    renderId: shortId.generate(),
    returns: null,
    returnState: DatalineStatus.STATUS_LOADING,
    status: 0,
    isReadOnly: false,
    isExternal: false,
  };

  return generateDatalineDefinition(dataline);
};

interface ChangeOperatorResult {
  updatedDatalines: OriginalDataline[] | null;
  datalineToRefresh: OriginalDataline | null;
}

export const changeConnectingOperatorHelper = (
  datalines: OriginalDataline[],
  comparisonRenderId: string,
  operator: LogicalOperator,
  definitionlineRenderId?: string | null,
): ChangeOperatorResult => {
  const processedDatalines = [...datalines];
  const positions = findDataPosition(
    datalines,
    definitionlineRenderId || comparisonRenderId,
  );
  const { datalineIndex, definitionlineIndex, comparisonIndex } = positions;

  if (datalineIndex === null) {
    return { updatedDatalines: null, datalineToRefresh: null };
  }

  // if both comparisonIndex and definitionlineRenderId are null, this is an
  // invalid state (i.e. dummy org unit), so we just return null.
  if (comparisonIndex === null && definitionlineRenderId === null) {
    return { updatedDatalines: null, datalineToRefresh: null };
  }

  const currentDefinitionlines =
    processedDatalines[datalineIndex].definitionlines;

  // the existence of a definitionlineRenderId is means an 'OR' operator between
  // two definition lines has been selected — we need to merge those two
  // definition lines together with the new operator.
  if (definitionlineRenderId) {
    if (
      !currentDefinitionlines?.length ||
      definitionlineIndex === null ||
      definitionlineIndex === 0
    ) {
      return { updatedDatalines: null, datalineToRefresh: null };
    }

    const combinedDefinitionline: {
      renderId: string;
      comparisons: ComparisonFrontend[];
    } = joinDefinitionlines(
      operator,
      currentDefinitionlines[definitionlineIndex - 1],
      currentDefinitionlines[definitionlineIndex],
    );

    currentDefinitionlines.splice(
      definitionlineIndex - 1,
      2,
      combinedDefinitionline,
    );
  } else if (operator === LogicalOperator.OR) {
    const newDefinitionline = splitDefinitionline(
      datalines[datalineIndex],
      positions,
    );
    if (newDefinitionline) {
      processedDatalines[datalineIndex] = newDefinitionline;
    }
  } else if (
    currentDefinitionlines &&
    definitionlineIndex !== null &&
    comparisonIndex !== null
  ) {
    currentDefinitionlines[definitionlineIndex].comparisons[
      comparisonIndex
    ].op = operator;
  }

  if (processedDatalines && positions.datalineIndex !== null) {
    const currentDataline = processedDatalines[positions.datalineIndex];
    currentDataline.isSaved = false;

    currentDataline.invalidities = validateDataline(currentDataline);
    currentDataline.status = DatalineStatus.STATUS_LOADING;
    currentDataline.returns = null;

    processedDatalines[positions.datalineIndex] =
      generateDatalineDefinition(currentDataline);

    return {
      updatedDatalines: processedDatalines,
      datalineToRefresh: currentDataline,
    };
  }

  return { updatedDatalines: null, datalineToRefresh: null };
};
